import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import apiService from "../services/api";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import PurchaseOrderForm from "../components/PurchaseOrderForm";
import toast from "react-hot-toast";
import Modal from "../components/ui/Modal";
import { ShoppingCart } from "lucide-react";

export default function CustomerProducts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: cartLoading, addToCart, cartCount } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [showPurchaseOrderForm, setShowPurchaseOrderForm] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [draftOrders, setDraftOrders] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null); // Track which draft is being edited
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [productQuantities, setProductQuantities] = useState({});
  const [quickViewQuantity, setQuickViewQuantity] = useState(1);

  const fetchProducts = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.category = selectedCategory;

      // Use public products endpoint for customers
      const data = await apiService.getPublicProducts(params);
      setProducts(data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchDraftOrders();
    fetchPendingApprovals();
  }, [fetchProducts]); // Add fetchProducts to dependency array

  // Check for editing drafts from localStorage (e.g., from CustomerOrders page)
  useEffect(() => {
    const editingDraftData = localStorage.getItem("editingDraft");
    if (editingDraftData) {
      try {
        const draft = JSON.parse(editingDraftData);
        setSelectedDraft(draft);
        setShowPurchaseOrderForm(true);
        localStorage.removeItem("editingDraft"); // Clear after use
      } catch (error) {
        console.error("Error parsing editing draft data:", error);
        localStorage.removeItem("editingDraft");
      }
    }
  }, []);

  // Refetch products when search terms or categories change
  useEffect(() => {
    if (!loading) {
      fetchProducts();
    }
  }, [fetchProducts, loading]);

  const fetchCategories = async () => {
    try {
      const data = await apiService.getPublicCategories();
      setCategories(data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchDraftOrders = async () => {
    try {
      const data = await apiService.getDraftOrders();
      setDraftOrders(data);
    } catch (error) {
      console.error("Failed to fetch draft orders:", error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const data = await apiService.getPendingApprovals();
      setPendingApprovals(data);
    } catch (error) {
      console.error("Failed to fetch pending approvals:", error);
    }
  };

  const handleApproveOrder = async (orderId, internalNotes = "") => {
    setApprovalLoading(true);
    try {
      await apiService.approveOrder(orderId, internalNotes);
      toast.success("Order approved successfully!");
      await fetchPendingApprovals();
    } catch (error) {
      console.error("Failed to approve order:", error);
      toast.error("Failed to approve order. Please try again.");
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleRejectOrder = async (
    orderId,
    rejectionReason,
    internalNotes = "",
  ) => {
    setApprovalLoading(true);
    try {
      await apiService.rejectOrder(orderId, rejectionReason, internalNotes);
      toast.success("Order rejected successfully!");
      await fetchPendingApprovals();
    } catch (error) {
      console.error("Failed to reject order:", error);
      toast.error("Failed to reject order. Please try again.");
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleSaveDraft = async (formData) => {
    setDraftLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.gstPercent || 18,
        })),
        purchaseOrderNumber: formData.poNumber,
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      if (selectedDraft) {
        // Update existing draft
        await apiService.updateDraft(selectedDraft.id, orderData);
        toast.success("Draft updated successfully!");
      } else {
        // Create new draft
        await apiService.saveDraft(orderData);
        toast.success("Draft saved successfully!");
      }

      await fetchDraftOrders();
      setShowPurchaseOrderForm(false);
      setSelectedDraft(null); // Clear selected draft
    } catch (error) {
      console.error("Failed to save draft:", error);
      toast.error(
        selectedDraft
          ? "Failed to update draft. Please try again."
          : "Failed to save draft. Please try again.",
      );
    } finally {
      setDraftLoading(false);
    }
  };

  const handleSubmitOrder = async (formData) => {
    setCheckoutLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.gstPercent || 18,
        })),
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        status: "submitted",
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      const order = await apiService.createOrder(orderData);

      toast.success(
        `Purchase Order submitted successfully! PO #${
          order.poNumber || order.id
        } is pending approval`,
      );

      setShowPurchaseOrderForm(false);
      setSelectedDraft(null); // Clear selected draft
    } catch (error) {
      console.error("Failed to submit order:", error);
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConfirmPO = async (formData) => {
    setCheckoutLoading(true);
    try {
      const orderData = {
        items: formData.items.map((item) => ({
          productSkuId: item.productSkuId || item.skuId,
          quantity: item.quantity,
          price: item.unitPrice,
          gstPercent: item.product?.gstPercent || 0,
        })),
        expectedDeliveryDate: formData.requiredDate,
        deliveryInstructions: formData.specialInstructions,
        specialRequirements: formData.notes,
        urgency: "normal",
        paymentTerms: formData.paymentTerms,
        customerNotes: formData.notes,
        status: "confirmed", // Direct confirmation
        deliveryAddress: formData.deliveryAddress,
        billingAddress: formData.billingAddress,
        authorizedBy: formData.authorizedBy,
        authorizedDate: formData.authorizedDate,
        authorizedSignature: formData.authorizedSignature,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        termsAndConditions: formData.termsAndConditions,
      };

      const order = await apiService.createOrder(orderData);

      setShowPurchaseOrderForm(false);
      setSelectedDraft(null); // Clear selected draft

      toast.success(
        `Purchase Order created successfully! PO #${
          order.poNumber || order.id
        } is pending approval`,
      );
      toast.success(
        "Your order has been submitted and is awaiting admin approval.",
      );
    } catch (error) {
      console.error("Failed to confirm order:", error);
      toast.error("Failed to confirm order. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleEditDraft = (draft) => {
    setSelectedDraft(draft);
    setShowPurchaseOrderForm(true);
  };

  const handleSubmitDraft = async (draftId) => {
    setDraftLoading(true);
    try {
      await apiService.submitDraft(draftId);
      await fetchDraftOrders();
      setSelectedDraft(null); // Clear selected draft

      toast.success("Draft submitted and approved successfully!");
    } catch (error) {
      console.error("Failed to submit draft:", error);
      toast.error("Failed to submit draft. Please try again.");
    } finally {
      setDraftLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (window.confirm("Are you sure you want to delete this draft?")) {
      try {
        await apiService.deleteOrder(draftId);
        await fetchDraftOrders();
        // Clear selected draft if it was the one being deleted
        if (selectedDraft && selectedDraft.id === draftId) {
          setSelectedDraft(null);
          setShowPurchaseOrderForm(false);
        }
        toast.success("Draft deleted successfully!");
      } catch (error) {
        console.error("Failed to delete draft:", error);
        toast.error("Failed to delete draft. Please try again.");
      }
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleClosePOForm = () => {
    setShowPurchaseOrderForm(false);
    setSelectedDraft(null); // Clear selected draft when closing
  };

  // Add this helper to extract customer info from user
  const getCustomerInfo = (user) => ({
    id: user?.id,
    name: user?.name || "",
    companyName: user?.businessName || "",
    // Contact information
    contactName: user?.contactName,
    contactRole: user?.contactRole,
    contactEmail: user?.contactEmail,
    contactMobile: user?.contactMobile,
    contactAltMobile: user?.contactAltMobile,
    // Billing address details
    billAddress: user?.billAddressLine || "",
    billCity: user?.billDistrict || "",
    billState: user?.billState || "",
    billZipCode: user?.billPinCode || "",
    billCountry: user?.billCountry || "India",
    billGpsLocation: user?.billGpsLocation,
    // Shipping address details
    shipAddress: user?.shipAddressLine || "",
    shipCity: user?.shipDistrict || "",
    shipState: user?.shipState || "",
    shipZipCode: user?.shipPinCode || "",
    shipCountry: user?.shipCountry || "India",
    shipGpsLocation: user?.shipGpsLocation,
    // Business details
    businessCategory: user?.businessCategory,
    gstNumber: user?.gstNumber,
    panNumber: user?.panNumber,
    yearsInBusiness: user?.yearsInBusiness,
    // Legacy fields for backward compatibility
    address: user?.billAddressLine || "",
    district: user?.billDistrict || "",
    city: user?.billDistrict || "",
    state: user?.billState || "",
    zipCode: user?.billPinCode || "",
    country: user?.billCountry || "India",
    phone: user?.contactMobile || "",
    email: user?.email || "",
  });

  // Helper functions for quantity management
  const getProductQuantity = (productId) => {
    return productQuantities[productId] || 1;
  };

  const setProductQuantity = (productId, quantity) => {
    setProductQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(1, quantity),
    }));
  };

  const handleQuickViewOpen = (product) => {
    setQuickViewProduct(product);
    setQuickViewQuantity(1);
    setCarouselIdx(0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
            Products
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Browse and order products
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              clearable
              onClear={() => setSearchTerm("")}
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={handleSearch} variant="primary">
            Search
          </Button>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((product) => {
          const images =
            product.imageUrls && product.imageUrls.length > 0
              ? product.imageUrls
              : [product.imageUrl];
          return (
            <div
              key={product.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col border border-gray-200 dark:border-gray-700"
            >
              {/* Product Image */}
              <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {images && images.length > 0 && images[carouselIdx] ? (
                  <img
                    src={images[carouselIdx]}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <span className="text-5xl text-gray-300 dark:text-gray-500">
                    📦
                  </span>
                )}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIdx(
                          (carouselIdx - 1 + images.length) % images.length,
                        );
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 bg-opacity-70 rounded-full p-1 shadow hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      ◀
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIdx((carouselIdx + 1) % images.length);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 bg-opacity-70 rounded-full p-1 shadow hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      ▶
                    </button>
                  </>
                )}
              </div>
              {/* Description */}
              {product.detailedDescription && (
                <div className="text-gray-700 dark:text-gray-300 text-xs bg-gray-50 dark:bg-gray-700/50 p-2 border-b border-gray-100 dark:border-gray-700 line-clamp-3">
                  {product.detailedDescription}
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {product.name}
                </h3>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-yellow-400">★</span>
                  <span className="text-yellow-400">★</span>
                  <span className="text-yellow-400">★</span>
                  <span className="text-yellow-400">★</span>
                  <span className="text-yellow-300">★</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    4.5
                  </span>
                </div>
                {product.brand && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                    Brand: {product.brand}
                  </div>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                  {product.description}
                </p>

                {/* SKU Information */}
                {product.skus && product.skus.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Available SKUs:
                    </div>
                    <div className="space-y-1">
                      {product.skus.slice(0, 3).map((sku) => (
                        <div
                          key={sku.id}
                          className="text-xs bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg border border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-blue-600 dark:text-blue-400">
                              {sku.sku}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400">
                              ₹{sku.sellingPrice || product.sellingPrice}
                            </span>
                          </div>
                          {sku.name && sku.name !== product.name && (
                            <div className="text-gray-600 dark:text-gray-400 mt-1">
                              {sku.name}
                            </div>
                          )}
                          {sku.description && (
                            <div className="text-gray-600 dark:text-gray-400 mt-1 text-xs line-clamp-2">
                              {sku.description}
                            </div>
                          )}
                          {sku.barcode && (
                            <div className="text-gray-500 dark:text-gray-500 text-xs mt-1">
                              Barcode: {sku.barcode}
                            </div>
                          )}
                        </div>
                      ))}
                      {product.skus.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          +{product.skus.length - 3} more SKU(s)
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    ₹{product.sellingPrice}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Min Qty: {product.minimumOrderQuantity || 1}
                  </span>
                </div>
                {/* SKU Selection */}
                {product.skus && product.skus.length > 1 && (
                  <div className="mb-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                      Select SKU:
                    </label>
                    <select
                      value={
                        productQuantities[`${product.id}_sku`] ||
                        product.skus[0]?.id ||
                        ""
                      }
                      onChange={(e) =>
                        setProductQuantity(`${product.id}_sku`, e.target.value)
                      }
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {product.skus.map((sku) => (
                        <option key={sku.id} value={sku.id}>
                          {sku.sku} - ₹
                          {sku.sellingPrice || product.sellingPrice}
                          {sku.name && sku.name !== product.name
                            ? ` (${sku.name})`
                            : ""}
                          {sku.description
                            ? ` - ${sku.description.substring(0, 50)}${
                                sku.description.length > 50 ? "..." : ""
                              }`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantity Selection */}
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Quantity:
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setProductQuantity(
                            product.id,
                            getProductQuantity(product.id) - 1,
                          )
                        }
                        className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold border border-gray-300 dark:border-gray-600"
                        disabled={getProductQuantity(product.id) <= 1}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={getProductQuantity(product.id)}
                        onChange={(e) =>
                          setProductQuantity(
                            product.id,
                            parseInt(e.target.value) || 1,
                          )
                        }
                        className="w-16 h-8 text-center border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() =>
                          setProductQuantity(
                            product.id,
                            getProductQuantity(product.id) + 1,
                          )
                        }
                        className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold border border-gray-300 dark:border-gray-600"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-auto">
                  <Button
                    onClick={() => handleQuickViewOpen(product)}
                    variant="outline"
                    size="sm"
                    className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Quick View
                  </Button>
                  <Button
                    onClick={() => {
                      const selectedSkuId =
                        productQuantities[`${product.id}_sku`] ||
                        product.skus?.[0]?.id ||
                        null;
                      addToCart(
                        product.id,
                        getProductQuantity(product.id),
                        selectedSkuId,
                      );
                    }}
                    variant="primary"
                    size="sm"
                    loading={cartLoading}
                    disabled={!product.isActive}
                    className="flex-1"
                  >
                    {!product.isActive ? "Not Available" : "Add to Cart"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {products.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No products found.</p>
        </div>
      )}

      {/* Draft Orders Section */}
      {draftOrders.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Draft Purchase Orders
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      PO Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {draftOrders.map((draft) => (
                    <tr
                      key={draft.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {draft.poNumber || draft.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(draft.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {draft.orderItems?.length || 0} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                        ₹{draft.totalAmount?.toFixed(2) || "0.00"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditDraft(draft)}
                            className="text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleSubmitDraft(draft.id)}
                            loading={draftLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Submit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteDraft(draft.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* PO Approvals Section */}
      {pendingApprovals.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Pending PO Approvals
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      PO Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {pendingApprovals.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {order.poNumber || order.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {order.orderItems?.length || 0} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                        ₹{order.totalAmount?.toFixed(2) || "0.00"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleApproveOrder(order.id)}
                            loading={approvalLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              const reason = prompt(
                                "Please enter rejection reason:",
                              );
                              if (reason) {
                                handleRejectOrder(order.id, reason);
                              }
                            }}
                            loading={approvalLoading}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Order Form Modal */}
      <PurchaseOrderForm
        isOpen={showPurchaseOrderForm}
        onClose={handleClosePOForm}
        cartItems={[]}
        onSubmit={handleSubmitOrder}
        onConfirmPO={handleConfirmPO}
        onSaveDraft={handleSaveDraft}
        draftOrders={draftOrders}
        draftLoading={draftLoading}
        customerInfo={getCustomerInfo(user)}
        draftOrder={selectedDraft} // Pass the selected draft for editing
      />

      {/* Quick View Modal */}
      <Modal
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        title={quickViewProduct?.name}
        size="lg"
      >
        {quickViewProduct && (
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0 w-full md:w-64 h-64 bg-gray-100 flex items-center justify-center rounded-lg overflow-hidden relative">
              {quickViewProduct.imageUrls &&
              quickViewProduct.imageUrls.length > 0 ? (
                <img
                  src={quickViewProduct.imageUrls[carouselIdx]}
                  alt={quickViewProduct.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-400 text-5xl">📦</span>
              )}
              {quickViewProduct.imageUrls &&
                quickViewProduct.imageUrls.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIdx(
                          (carouselIdx -
                            1 +
                            quickViewProduct.imageUrls.length) %
                            quickViewProduct.imageUrls.length,
                        );
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-1 shadow hover:bg-gray-100"
                    >
                      ◀
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIdx(
                          (carouselIdx + 1) % quickViewProduct.imageUrls.length,
                        );
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-1 shadow hover:bg-gray-100"
                    >
                      ▶
                    </button>
                  </>
                )}
            </div>
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-yellow-400">★</span>
                <span className="text-yellow-400">★</span>
                <span className="text-yellow-400">★</span>
                <span className="text-yellow-400">★</span>
                <span className="text-yellow-300">★</span>
                <span className="text-xs text-gray-500 ml-1">4.5</span>
              </div>
              {quickViewProduct.brand && (
                <div className="text-xs text-blue-700 font-medium mb-1">
                  Brand: {quickViewProduct.brand}
                </div>
              )}
              <div className="text-lg font-bold text-blue-600 mb-2">
                ₹{quickViewProduct.sellingPrice}
              </div>
              <div className="text-sm text-gray-500 mb-2">
                Minimum Order Qty: {quickViewProduct.minimumOrderQuantity || 1}
              </div>

              {/* SKU Information in Quick View */}
              {quickViewProduct.skus && quickViewProduct.skus.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Available SKUs:
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {quickViewProduct.skus.map((sku, index) => (
                      <div
                        key={sku.id}
                        className="bg-gray-50 p-3 rounded border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-blue-600">
                              {sku.sku}
                            </div>
                            {sku.name && sku.name !== quickViewProduct.name && (
                              <div className="text-gray-600 text-sm">
                                {sku.name}
                              </div>
                            )}
                            {sku.description && (
                              <div className="text-gray-600 text-sm mt-1">
                                {sku.description}
                              </div>
                            )}
                            {sku.barcode && (
                              <div className="text-gray-500 text-xs mt-1">
                                Barcode: {sku.barcode}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900">
                              ₹
                              {sku.sellingPrice ||
                                quickViewProduct.sellingPrice}
                            </div>
                            {sku.weight && (
                              <div className="text-xs text-gray-500">
                                Weight: {sku.weight}kg
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {quickViewProduct?.detailedDescription && (
                <div className="text-gray-700 text-sm bg-gray-50 rounded p-3 mb-3 whitespace-pre-line border border-gray-200">
                  {quickViewProduct.detailedDescription}
                </div>
              )}

              {/* Quantity Selection */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Quantity:
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setQuickViewQuantity(Math.max(1, quickViewQuantity - 1))
                      }
                      className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold border border-gray-300"
                      disabled={quickViewQuantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quickViewQuantity}
                      onChange={(e) =>
                        setQuickViewQuantity(
                          Math.max(1, parseInt(e.target.value) || 1),
                        )
                      }
                      className="w-20 h-8 text-center border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() =>
                        setQuickViewQuantity(quickViewQuantity + 1)
                      }
                      className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold border border-gray-300"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  addToCart(quickViewProduct.id, quickViewQuantity);
                  setQuickViewProduct(null);
                }}
                variant="primary"
                size="md"
                disabled={!quickViewProduct.isActive}
                className="w-full"
              >
                {!quickViewProduct.isActive ? "Not Available" : "Add to Cart"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
